"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const TermsOfService = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const renderList = (key: string) => {
    const items = t(key, { returnObjects: true });
    if (!Array.isArray(items)) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  };

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const SubSection = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4 ml-0">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">
        {String(t(titleKey))}
      </h3>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  const P = ({ keyName }: { keyName: string }) => <p>{String(t(keyName))}</p>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("terms.title"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {String(t("terms.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <div className="mb-8 p-4 rounded-lg bg-muted/50 text-muted-foreground text-sm space-y-1">
              <p>{String(t("terms.intro"))}</p>
              <p className="font-medium text-foreground mt-2">{String(t("terms.company"))}</p>
              <p>{String(t("terms.address"))}</p>
              <p>{String(t("terms.siren"))}</p>
              <p>{String(t("terms.vat"))}</p>
              <p className="mt-2">{String(t("terms.intro2"))}</p>
              <p>{String(t("terms.intro3"))}</p>
            </div>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="terms.section1Title">
                <P keyName="terms.section1P1" />
                {renderList("terms.section1List")}
                <P keyName="terms.section1P2" />
              </Section>

              <Section titleKey="terms.section2Title">
                <SubSection titleKey="terms.section2_1Title">
                  <P keyName="terms.section2_1P1" />
                  {renderList("terms.section2_1List")}
                </SubSection>
                <SubSection titleKey="terms.section2_2Title">
                  {renderList("terms.section2_2List")}
                </SubSection>
                <SubSection titleKey="terms.section2_3Title">
                  <P keyName="terms.section2_3P1" />
                  <P keyName="terms.section2_3P2" />
                </SubSection>
              </Section>

              <Section titleKey="terms.section3Title">
                {renderList("terms.section3List")}
              </Section>

              <Section titleKey="terms.section4Title">
                <P keyName="terms.section4P1" />
                <P keyName="terms.section4P2" />
                <P keyName="terms.section4P3" />
                {renderList("terms.section4List1")}
                <P keyName="terms.section4P4" />
                {renderList("terms.section4List2")}
              </Section>

              <Section titleKey="terms.section5Title">
                <P keyName="terms.section5P1" />
                <P keyName="terms.section5P2" />
                <P keyName="terms.section5P3" />
                <P keyName="terms.section5P4" />
              </Section>

              <Section titleKey="terms.section6Title">
                <P keyName="terms.section6P1" />
                <P keyName="terms.section6P2" />
                {renderList("terms.section6List")}
                <P keyName="terms.section6P3" />
                <P keyName="terms.section6P4" />
              </Section>

              <Section titleKey="terms.section7Title">
                <P keyName="terms.section7P1" />
                {renderList("terms.section7List")}
                <P keyName="terms.section7P2" />
              </Section>

              <Section titleKey="terms.section8Title">
                <P keyName="terms.section8P1" />
                <P keyName="terms.section8P2" />
                <P keyName="terms.section8P3" />
              </Section>

              <Section titleKey="terms.section9Title">
                <P keyName="terms.section9P1" />
                {renderList("terms.section9List")}
                <P keyName="terms.section9P2" />
                <P keyName="terms.section9P3" />
              </Section>

              <Section titleKey="terms.section10Title">
                <P keyName="terms.section10P1" />
                <P keyName="terms.section10P2" />
              </Section>

              <Section titleKey="terms.section11Title">
                <P keyName="terms.section11P1" />
                {renderList("terms.section11List")}
                <P keyName="terms.section11P2" />
              </Section>

              <Section titleKey="terms.section12Title">
                <P keyName="terms.section12P1" />
                <P keyName="terms.section12P2" />
                {renderList("terms.section12List")}
                <P keyName="terms.section12P3" />
              </Section>

              <Section titleKey="terms.section13Title">
                <P keyName="terms.section13P1" />
                {renderList("terms.section13List")}
                <P keyName="terms.section13P2" />
              </Section>

              <Section titleKey="terms.section14Title">
                <P keyName="terms.section14P1" />
                {renderList("terms.section14List")}
              </Section>

              <Section titleKey="terms.section15Title">
                <P keyName="terms.section15P1" />
              </Section>

              <Section titleKey="terms.section16Title">
                <P keyName="terms.section16P1" />
                <P keyName="terms.section16P2" />
              </Section>

              <Section titleKey="terms.section17Title">
                <P keyName="terms.section17P1" />
                <P keyName="terms.section17P2" />
                <P keyName="terms.section17P3" />
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
